import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { StockLocationPromptService } from './stock-location-prompt.service';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private baseUrl = environment.apiUrl;

    constructor(private http: HttpClient, private injector: Injector) { }

    get<T>(endpoint: string): Observable<T> {
        return this.http.get<T>(`${this.baseUrl}/${endpoint}`);
    }

    post<T>(endpoint: string, body: any): Observable<T> {
        return this.withLocation<T>('POST', endpoint, body);
    }

    put<T>(endpoint: string, body: any): Observable<T> {
        return this.withLocation<T>('PUT', endpoint, body);
    }

    delete<T>(endpoint: string): Observable<T> {
        return this.withLocation<T>('DELETE', endpoint);
    }

    private withLocation<T>(method: string, endpoint: string, body?: any): Observable<T> {
        const send = (path: string, data?: any) => this.http.request<T>(method, `${this.baseUrl}/${path}`, { body: data });
        return send(endpoint, body).pipe(catchError(error => {
            // Only a backend-confirmed, rolled-back physical stock change opens this field.
            // Basic/Advance/INACTIVE responses and unrelated errors pass through unchanged.
            if (error?.error?.error_code !== 'STORAGE_LOCATION_REQUIRED' || endpoint.startsWith('production/')) return throwError(() => error);
            const query = new URLSearchParams(endpoint.split('?')[1] || '');
            const dId = Number(body?.d_id || query.get('d_id'));
            if (!dId) return throwError(() => error);
            const stockClass = endpoint.startsWith('milk_managements/') || endpoint.startsWith('billing_and_reports/') ? 'MILK' : 'PRODUCT';
            return this.injector.get(StockLocationPromptService).choose(dId, stockClass).pipe(switchMap(locationId => {
                if (method === 'DELETE') {
                    query.set('storage_location_id', String(locationId));
                    return send(`${endpoint.split('?')[0]}?${query.toString()}`);
                }
                return send(endpoint, { ...body, storage_location_id: locationId });
            }));
        }));
    }
}
